/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import { useCallback, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useAuth } from "auth/Auth";

const CASE_EMAIL_TEMPLATES = [
  {
    key: "acknowledgement",
    label: "Case created by the customer",
    hint: "Sent when a case arrives by email or from the website.",
  },
  {
    key: "agent_created",
    label: "Case created by support",
    hint: "Sent when an agent creates the case in the CRM or from a phone call.",
  },
  {
    key: "awaiting_customer",
    label: "Awaiting customer information",
    hint: "Sent when the status changes to Awaiting Customer Information.",
  },
  {
    key: "closed",
    label: "Case closed",
    hint: "Sent when the case is closed. {{comment}} is the resolution note.",
  },
  {
    key: "new_comment",
    label: "New public comment",
    hint: "Sent when a staff comment is added. {{comment}} is that comment.",
  },
];

function emptyCaseEmailTemplates() {
  return Object.fromEntries(
    CASE_EMAIL_TEMPLATES.map((field) => [field.key, { subject: "", body: "" }])
  );
}

export default function CaseEmailTemplatesSettings() {
  const { axiosInstance } = useAuth();
  const [templates, setTemplates] = useState(() => emptyCaseEmailTemplates());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/email-to-case/settings");
      setTemplates({
        ...emptyCaseEmailTemplates(),
        ...(res.data?.notification_templates || {}),
      });
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Unable to load case email templates.");
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await axiosInstance.put("/email-to-case/settings", {
        notification_templates: templates,
      });
      setSuccess("Case email templates saved.");
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Unable to save case email templates.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <MDTypography variant="body2" color="text">
        Loading case email templates…
      </MDTypography>
    );
  }

  return (
    <MDBox>
      <MDTypography variant="h6" fontWeight="medium" sx={{ fontSize: "1rem" }} mb={0.5}>
        Case email templates
      </MDTypography>
      <MDTypography variant="body2" color="text" mb={2}>
        Emails customers receive for a case. Placeholders: {"{{case_number}}"}, {"{{first_name}}"}, {"{{last_name}}"}, {"{{comment}}"}.
        A blank subject or body uses the built-in wording.
      </MDTypography>
      <Snackbar
        open={Boolean(success || error)}
        autoHideDuration={4000}
        onClose={() => {
          setSuccess("");
          setError("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={error ? "error" : "success"}
          onClose={() => {
            setSuccess("");
            setError("");
          }}
          sx={{ width: "100%" }}
        >
          {error || success}
        </Alert>
      </Snackbar>
      {CASE_EMAIL_TEMPLATES.map((field) => (
        <MDBox key={field.key} mb={2.5}>
          <MDTypography variant="button" display="block" mb={0.5}>
            {field.label}
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block" mb={1}>
            {field.hint}
          </MDTypography>
          <TextField
            label="Subject"
            fullWidth
            size="small"
            value={templates[field.key]?.subject || ""}
            onChange={(event) =>
              setTemplates((current) => ({
                ...current,
                [field.key]: { ...current[field.key], subject: event.target.value },
              }))
            }
            sx={{ mb: 1 }}
          />
          <TextField
            label="Body"
            fullWidth
            multiline
            minRows={4}
            value={templates[field.key]?.body || ""}
            onChange={(event) =>
              setTemplates((current) => ({
                ...current,
                [field.key]: { ...current[field.key], body: event.target.value },
              }))
            }
          />
        </MDBox>
      ))}
      <MDButton variant="gradient" color="info" disabled={saving} onClick={save}>
        {saving ? "Saving…" : "Save templates"}
      </MDButton>
    </MDBox>
  );
}
